import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, List, Switch, Tag } from 'antd';
import { projectsApi } from '../projects.api';
import type { Assignment } from '../types';

export function ProjectMembersTab({
  orgId,
  projectId,
  canManage,
}: {
  orgId: string;
  projectId: string;
  canManage: boolean;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const key = ['assignments', orgId, projectId] as const;

  const query = useQuery({
    queryKey: key,
    queryFn: () => projectsApi.listAssignments(orgId, projectId),
  });

  const mutation = useMutation({
    mutationFn: ({ userId, assigned }: { userId: string; assigned: boolean }) =>
      assigned
        ? projectsApi.assign(orgId, projectId, userId)
        : projectsApi.unassign(orgId, projectId, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      message.success('Assignment updated');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to update assignment'),
  });

  return (
    <List<Assignment>
      loading={query.isLoading}
      dataSource={query.data ?? []}
      renderItem={(a) => (
        <List.Item
          actions={[
            a.role === 'owner' ? (
              <Tag key="all" color="gold">
                All projects
              </Tag>
            ) : (
              <Switch
                key="sw"
                checked={a.assigned}
                disabled={!canManage || mutation.isPending}
                onChange={(assigned) => mutation.mutate({ userId: a.userId, assigned })}
              />
            ),
          ]}
        >
          <List.Item.Meta
            title={
              <>
                {a.name} <Tag>{a.role}</Tag>
              </>
            }
            description={a.email}
          />
        </List.Item>
      )}
    />
  );
}
